import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/* eslint-disable react-refresh/only-export-components */

type SiteChromeContextValue = {
  /** When true, show the global header even on routes that normally hide it (e.g. Portal gate). */
  forceShowGlobalHeader: boolean;
  setForceShowGlobalHeader: (show: boolean) => void;
};

const SiteChromeContext = createContext<SiteChromeContextValue | undefined>(undefined);

export function SiteChromeProvider({ children }: { children: ReactNode }) {
  const [forceShowGlobalHeader, setForceShowGlobalHeaderState] = useState(false);

  const setForceShowGlobalHeader = useCallback((show: boolean) => {
    setForceShowGlobalHeaderState(show);
  }, []);

  const value = useMemo(
    () => ({
      forceShowGlobalHeader,
      setForceShowGlobalHeader,
    }),
    [forceShowGlobalHeader, setForceShowGlobalHeader]
  );

  return <SiteChromeContext.Provider value={value}>{children}</SiteChromeContext.Provider>;
}

function useSiteChromeContext(): SiteChromeContextValue {
  const ctx = useContext(SiteChromeContext);
  if (!ctx) {
    throw new Error('useSiteChromeContext must be used within SiteChromeProvider');
  }
  return ctx;
}

/** Show the global site header on partner-portal while this component is mounted (gate page). */
export function useForceGlobalHeader(show = true): void {
  const { setForceShowGlobalHeader } = useSiteChromeContext();

  useEffect(() => {
    setForceShowGlobalHeader(show);
    return () => setForceShowGlobalHeader(false);
  }, [show, setForceShowGlobalHeader]);
}

export function useSiteChrome(): Pick<SiteChromeContextValue, 'forceShowGlobalHeader'> {
  return useSiteChromeContext();
}
